import { memo, useCallback } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import type { CrmWhatsappAccountConfig } from "../../statistics/types";
import { useCrmWhatsAppAccountConfigForm } from "../hooks/useCrmWhatsAppAccountConfigForm";
import CrmWhatsAppAccountConfigForm from "./CrmWhatsAppAccountConfigForm";

export interface CrmWhatsAppAccountConfigDialogProps {
  open: boolean;
  config: CrmWhatsappAccountConfig | null;
  onClose: () => void;
  onSubmit: (input: CrmWhatsappAccountConfig) => Promise<boolean>;
}

/**
 * Diálogo de configurações de conta WhatsApp (protótipo).
 *
 * Responsabilidade:
 * - orquestrar abertura/fechamento
 * - renderizar UI do formulário (delegado para `CrmWhatsAppAccountConfigForm`)
 */
const CrmWhatsAppAccountConfigDialog = memo<CrmWhatsAppAccountConfigDialogProps>(
  ({ open, config, onClose, onSubmit }) => {
    const form = useCrmWhatsAppAccountConfigForm({
      open,
      config,
      onSubmit,
      onSaved: onClose,
    });

    const handleClose = useCallback(() => {
      if (form.isSubmitting) return;
      onClose();
    }, [form.isSubmitting, onClose]);

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Configurações de conta WhatsApp
          <IconButton
            aria-label="Fechar"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Este é um formulário de protótipo para planejar a edição de
              dados. Em breve, estes campos serão carregados e persistidos via
              API.
            </Typography>

            {form.hasSubmitError && (
              <Alert severity="error">
                Erro ao salvar configurações. Tente novamente.
              </Alert>
            )}

            <CrmWhatsAppAccountConfigForm form={form} />
          </Stack>
        </DialogContent>

        <DialogActions>
          {config && (
            <Button
              color="error"
              disabled={form.isSubmitting}
              sx={{ textTransform: "none", mr: "auto" }}
              // Ação de remoção será conectada à API real em uma próxima etapa.
              onClick={handleClose}
            >
              Remover configuração
            </Button>
          )}

          <Button
            onClick={handleClose}
            color="inherit"
            disabled={form.isSubmitting}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              void form.submit();
            }}
            disabled={form.isSubmitting || !config}
            sx={{ textTransform: "none" }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

CrmWhatsAppAccountConfigDialog.displayName =
  "CrmWhatsAppAccountConfigDialog";

export default CrmWhatsAppAccountConfigDialog;

